import type { ProductInformation, Swatches } from '@dealerportal/common';
import { toCicLocale } from '@dealerportal/i18n';
import { useLocaleSafe } from '@dealerportal/i18n/client';
import {
  buildProductInformation,
  cicMerchandisingTags,
  extractItemGroups,
  type SpecBuilderOptions,
} from '@dealerportal/utilities';
import uniq from 'es-toolkit/compat/uniq';
import { useCallback, useEffect, useState } from 'react';
import { decodeItemCode } from '../../../../utilities/src/lib/utilities';
import { useCatalogContext, useRequireCiC } from '../Contexts/CatalogContext';
import { CiCFrontendAdapter } from '../Mappers/CiCFrontendAdapter';
import { fetchCiCFeatures } from '../Mappers/cicFeatures';
import { fetchVariants } from './useCiCSearch';

type CiCItemLookupResult = {
  products: ProductInformation[];
  isLoading: boolean;
};

const adapter = new CiCFrontendAdapter();

const localizationsCache: Record<string, Record<string, string>> = {};

/**
 * CiC's `characteristic.value` -> label dictionary for `locale` (lowercase
 * `xx-yy`, as the CDN names the files), cached per locale for the session.
 */
async function fetchLocalizations(
  locale = 'en-us'
): Promise<Record<string, string>> {
  const cached = localizationsCache[locale];
  if (cached) return cached;
  const scriptUrl = process.env.NEXT_PUBLIC_CIC_CLIENT_SCRIPT_URL ?? '';
  if (!scriptUrl) return {};
  const baseUrl = new URL('../', scriptUrl).href;
  const manifest: Record<string, string> = await fetch(
    `${baseUrl}manifest.json`
  ).then((r) => r.json());
  const path = manifest[`localizations/localizations.${locale}.json`];
  if (!path) {
    localizationsCache[locale] = {};
    return {};
  }
  const result: Record<string, string> = await fetch(`${baseUrl}${path}`).then(
    (r) => r.json()
  );
  localizationsCache[locale] = result;
  return result;
}

let catalogConfigCache: SpecBuilderOptions['config'];

async function fetchCatalogConfig(): Promise<SpecBuilderOptions['config']> {
  if (catalogConfigCache) return catalogConfigCache;
  const url =
    process.env.NEXT_PUBLIC_CATALOG_CONFIG_URL ?? '/config/catalog.json';
  const result: SpecBuilderOptions['config'] = await fetch(url).then((r) =>
    r.json()
  );
  catalogConfigCache = result;
  return result;
}

export const useCiCItemLookup = (itemCodes: string[]): CiCItemLookupResult => {
  const [products, setProducts] = useState<ProductInformation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { loaded, catalogVersionId } = useCatalogContext();
  const locale = useLocaleSafe();

  const fetchItem = useCallback(
    async (catalogVersionId: string, itemCode: string) => {
      const res = await globalThis.CiCAPI.content.v2.getItemVariant({
        itemId: `${catalogVersionId}.${itemCode}`,
      });

      const item = res.result;

      if (item == null) return null;

      const features = await fetchCiCFeatures([{ itemId: item.id }], {
        onlyVisibleSelectable: true,
      });

      const normalizedItem = adapter.adaptItem(item);

      const [itemGroupsResult, localizations, config] = await Promise.all([
        globalThis.CiCAPI.content.v2.getCatalogVersionItemGroups(
          catalogVersionId
        ),
        fetchLocalizations(toCicLocale(locale).toLowerCase()),
        fetchCatalogConfig(),
      ]);
      const normalizedGroups = adapter.adaptGroups(
        itemGroupsResult.result ?? []
      );

      if (normalizedGroups.length === 0) {
        console.warn(
          `No item groups found for catalog version ID: ${catalogVersionId}`
        );
      }

      const { brandGroup, productGroup } = extractItemGroups(
        normalizedItem,
        normalizedGroups
      );

      let { result: variants = [] } =
        await globalThis.CiCAPI.content.v2.getItemVariantProposals(
          { itemId: item.id },
          { variesBy: 'style' }
        );

      if (variants.length === 0) {
        variants = await fetchVariants(catalogVersionId, [
          productGroup?.id ?? '',
        ]);
      } else {
        variants.unshift(item);
      }

      // Sort them based on sequenceOrder
      variants.sort(
        (a, b) => (a.sequenceOrder ?? -1) - (b.sequenceOrder ?? -1)
      );

      // HACK: temporarily use the merchandising context from variant
      const merchandising =
        variants.find((variant) => variant.id === normalizedItem.id)?.context
          ?.merchandising ?? [];
      normalizedItem.context = { merchandising };

      // Create swatches from variants
      const swatches: Swatches[] = await Promise.all(
        variants.map(async (variant) => {
          const { slug: variantSlug, sku: variantSku } = decodeItemCode(
            variant.code
          );
          const fullVariant = await globalThis.CiCAPI.content.v2.getItemVariant(
            {
              itemId: `${catalogVersionId}.${variant.code}`,
            }
          );
          const { tags, hasSample, hasVisualization } = cicMerchandisingTags(
            fullVariant.result?.context?.merchandising
          );

          return {
            colorName: variant.names?.main ?? '',
            imageURL: variant.images?.main?.uri ?? '',
            slug: variantSlug,
            sku: variantSku,
            UPID: variant.refCodes?.manufCode ?? '',
            hasVisualization,
            hasSample,
            tags: uniq(tags),
            unitPrice: Number(variant.refCodes?.others?.pricing ?? 0),
          };
        })
      );

      const info = buildProductInformation(
        normalizedItem,
        {
          brandName: brandGroup?.name || '',
          productName: productGroup?.name ?? '',
          swatches,
          features,
        },
        { localizations, config }
      );
      return info;
    },
    [locale]
  );

  // Use a stable string key so the effect doesn't re-fire on every render
  // when the caller passes a freshly-created array with the same contents.
  const itemCodesKey = itemCodes.join(',');
  useRequireCiC(itemCodesKey !== '');

  useEffect(() => {
    if (!loaded || !catalogVersionId) return;
    // The item codes can change while a lookup is in flight (a colour swapped
    // in place), so only the latest lookup may set the result.
    let cancelled = false;
    setIsLoading(true);
    Promise.all(
      itemCodesKey
        .split(',')
        .filter(Boolean)
        .map((itemCode) => fetchItem(catalogVersionId, itemCode))
    )
      .then((results) => {
        const validResults = results.filter(
          (result): result is ProductInformation => result !== null
        );
        if (!cancelled) setProducts(validResults);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loaded, catalogVersionId, itemCodesKey, fetchItem]);

  return {
    products,
    isLoading,
  };
};
